// ============================================================================
// AdminLayout — persistent shell for the UIF admin app (Round 4 redesign).
// ============================================================================
// Replaces the flat top-bar of the old AdminPanel with a sidebar + slim top
// bar. Each admin route renders into the main slot via the `children` prop.
// MainApp.tsx is responsible for picking which panel to mount; AdminLayout
// only owns the chrome (nav, branding, sign-out, breadcrumb, quick links).
//
// Sidebar items map 1:1 to AdminRoute values in MainApp.tsx. Active item is
// derived from the prop `current`, not from window.location — that lets the
// layout work for both real routes and for previews (e.g. tests).
//
// Mobile: the sidebar collapses to a hamburger drawer at < 768px (controlled
// by the `mobileOpen` state). Phase 2 will polish this further; for now the
// drawer slides in from the left and clicking outside dismisses it.
// ============================================================================

import { useState, useEffect, useRef, type ReactNode } from 'react';
import {
  LayoutDashboard, CalendarDays, Users, Repeat, Mic2, MapPin, Tag,
  DoorOpen, BarChart3, Settings as SettingsIcon, LogOut, ExternalLink,
  QrCode, Menu, X, Plus, Upload, DollarSign, Users2, Mail, FileText, Wheat,
  Tractor, PenLine, FileSignature, Landmark, ClipboardList, LayoutGrid, Grid3x3,
  Sprout, ClipboardCheck,
} from 'lucide-react';
import type { AuthUser } from '../api';

export type AdminSection =
  | 'dashboard' | 'events' | 'registrations' | 'series'
  | 'presenters' | 'locations' | 'resources' | 'bookings' | 'tags'
  | 'budgets' | 'reports' | 'subscribers' | 'pages' | 'farmers' | 'farms' | 'documents'
  | 'envelopes' | 'agreements' | 'programs' | 'enrollments' | 'tracks' | 'milestonedefs'
  | 'practices' | 'cpa52' | 'settings';

interface NavItem {
  id: AdminSection;
  label: string;
  icon: ReactNode;
  badge?: number | string;
  enabled: boolean;          // false = grayed out + "soon" tag
}

interface AdminLayoutProps {
  current: AdminSection;
  user: AuthUser;
  onSignOut: () => void;
  /** Optional badge count to render next to a sidebar item (e.g. unread). */
  registrationsBadge?: number;
  children: ReactNode;
}

export function AdminLayout({ current, user, onSignOut, registrationsBadge, children }: AdminLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Task #7: drawer polish.
  //   - ESC key closes the drawer.
  //   - Body scroll lock while open (otherwise the page underneath scrolls
  //     when the user tries to scroll the drawer near its top/bottom).
  //   - Swipe-left-to-close gesture (touch start/end x-delta heuristic).
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while drawer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  // Track touch x for swipe-to-close. We only close on a clear leftward
  // swipe (>= 60px) initiated within the drawer.
  const touchStartXRef = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartXRef.current;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    if (start - end >= 60) setMobileOpen(false);
    touchStartXRef.current = null;
  };

  const items: NavItem[] = [
    { id: 'dashboard',     label: 'Dashboard',            icon: <LayoutDashboard className="h-4 w-4" />, enabled: true },
    { id: 'events',        label: 'Events',               icon: <CalendarDays className="h-4 w-4" />,    enabled: true },
    { id: 'registrations', label: 'Registrations',        icon: <Users className="h-4 w-4" />,           enabled: true, badge: registrationsBadge },
    { id: 'series',        label: 'Series and templates', icon: <Repeat className="h-4 w-4" />,          enabled: true },
    { id: 'presenters',    label: 'Presenters',           icon: <Mic2 className="h-4 w-4" />,            enabled: true },
    { id: 'locations',     label: 'Locations',            icon: <MapPin className="h-4 w-4" />,          enabled: true },
    { id: 'resources',     label: 'Resources',            icon: <Users2 className="h-4 w-4" />,          enabled: true },
    { id: 'tags',          label: 'Tags',                 icon: <Tag className="h-4 w-4" />,             enabled: true },
    { id: 'bookings',      label: 'Room bookings',        icon: <DoorOpen className="h-4 w-4" />,        enabled: true },
    { id: 'budgets',       label: 'Budgets',              icon: <DollarSign className="h-4 w-4" />,      enabled: true },
    { id: 'subscribers',   label: 'Subscribers',          icon: <Mail className="h-4 w-4" />,            enabled: true },
    { id: 'farmers',       label: 'Producers',            icon: <Wheat className="h-4 w-4" />,           enabled: true },
    { id: 'farms',         label: 'Farms',                icon: <Tractor className="h-4 w-4" />,         enabled: true },
    { id: 'documents',     label: 'Documents',            icon: <FileText className="h-4 w-4" />,        enabled: true },
    { id: 'envelopes',     label: 'Signatures',           icon: <PenLine className="h-4 w-4" />,         enabled: true },
    { id: 'agreements',    label: 'Agreements',           icon: <FileSignature className="h-4 w-4" />,   enabled: true },
    { id: 'programs',      label: 'Programs',             icon: <Landmark className="h-4 w-4" />,        enabled: true },
    { id: 'enrollments',   label: 'Enrollments',          icon: <ClipboardList className="h-4 w-4" />,   enabled: true },
    { id: 'tracks',        label: 'Track plans',          icon: <LayoutGrid className="h-4 w-4" />,      enabled: true },
    { id: 'milestonedefs', label: 'Milestone defs',       icon: <Grid3x3 className="h-4 w-4" />,         enabled: true },
    { id: 'practices',     label: 'Practices',            icon: <Sprout className="h-4 w-4" />,          enabled: true },
    { id: 'cpa52',         label: 'CPA-52',               icon: <ClipboardCheck className="h-4 w-4" />,  enabled: true },
    { id: 'pages',         label: 'Pages',                icon: <FileText className="h-4 w-4" />,        enabled: true },
  ];

  const lowerItems: NavItem[] = [
    { id: 'reports',  label: 'Reports',  icon: <BarChart3 className="h-4 w-4" />, enabled: true },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon className="h-4 w-4" />, enabled: false },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile drawer backdrop — always rendered so we can fade it in/out
          via CSS opacity transition rather than a hard pop. */}
      <div
        className={
          'fixed inset-0 z-30 bg-black/40 md:hidden transition-opacity duration-200 ' +
          (mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none')
        }
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className={
          'fixed md:sticky top-0 left-0 z-40 h-screen w-72 sm:w-60 bg-white border-r border-gray-200 ' +
          'flex flex-col flex-shrink-0 transition-transform duration-200 ease-out shadow-xl md:shadow-none ' +
          (mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
        }
      >
        {/* Brand header */}
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-uif-green text-white flex items-center justify-center text-xs font-semibold">
              UIF
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-uif-green-dark">SmartFarmed</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">admin</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1 text-gray-500 hover:text-gray-700"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3" aria-label="Admin navigation">
          <div className="px-2">
            {items.map((it) => (
              <NavLink key={it.id} item={it} active={it.id === current} onClick={() => setMobileOpen(false)} />
            ))}
          </div>

          <div className="border-t border-gray-200 mt-3 mx-4" />

          <div className="px-2 mt-3">
            {lowerItems.map((it) => (
              <NavLink key={it.id} item={it} active={it.id === current} onClick={() => setMobileOpen(false)} />
            ))}
          </div>
        </nav>

        {/* User footer + sign out */}
        <div className="border-t border-gray-200 px-3 py-3 space-y-2">
          <div className="px-1">
            <div className="text-xs font-medium text-gray-700 truncate">
              {user.display_name || user.username}
            </div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">{user.role}</div>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-100"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-1.5 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="text-xs text-gray-500">
              <span className="hidden sm:inline">UIF admin · </span>
              <span className="font-medium text-gray-700">{labelFor(current)}</span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* S3 chore-ledger item 4: mobile had NO sign-out affordance — the
                only one lived in the drawer footer. Always-visible on <md. */}
            <button
              type="button"
              onClick={onSignOut}
              className="md:hidden inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">Sign out</span>
            </button>
            <button
              type="button"
              onClick={() => (window.location.hash = '#admin/events/new')}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
            >
              <Plus className="h-4 w-4" /> Add event
            </button>
            <button
              type="button"
              onClick={() => (window.location.hash = '#admin/events/bulk')}
              className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Upload className="h-4 w-4" /> Bulk upload
            </button>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              title="Open the public events site"
            >
              <ExternalLink className="h-4 w-4" /> Public site
            </a>
            <a
              href="/#agc"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              title="Open the Training Center room calendar"
            >
              <QrCode className="h-4 w-4" /> Room calendar
            </a>
          </div>
        </header>

        {/* Page slot */}
        <main className="flex-1 p-4 md:p-6 max-w-6xl w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

function NavLink({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  const inner = (
    <div
      className={
        'flex items-center gap-3 px-3 py-2 text-sm rounded-md mb-0.5 ' +
        (active
          ? 'bg-uif-green/10 text-uif-green-dark font-semibold'
          : item.enabled
            ? 'text-gray-700 hover:bg-gray-100'
            : 'text-gray-400 cursor-not-allowed')
      }
    >
      <span className="flex-shrink-0">{item.icon}</span>
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge != null && (
        <span className={
          'text-[10px] px-1.5 py-0.5 rounded-full font-medium ' +
          (active ? 'bg-uif-green text-white' : 'bg-gray-200 text-gray-600')
        }>
          {item.badge}
        </span>
      )}
      {!item.enabled && (
        <span className="text-[10px] text-gray-400 italic">soon</span>
      )}
    </div>
  );

  if (!item.enabled) return inner;

  return (
    <a
      href={`#admin/${item.id}`}
      onClick={onClick}
      className="block"
    >
      {inner}
    </a>
  );
}

function labelFor(section: AdminSection): string {
  switch (section) {
    case 'dashboard':     return 'Dashboard';
    case 'events':        return 'Events';
    case 'registrations': return 'Registrations';
    case 'series':        return 'Series and templates';
    case 'presenters':    return 'Presenters';
    case 'locations':     return 'Locations';
    case 'resources':     return 'Resources';
    case 'tags':          return 'Tags';
    case 'bookings':      return 'Room bookings';
    case 'budgets':       return 'Budgets';
    case 'reports':       return 'Reports';
    case 'subscribers':   return 'Subscribers';
    case 'farmers':       return 'Producers';
    case 'farms':         return 'Farms';
    case 'documents':     return 'Documents';
    case 'envelopes':     return 'Signatures';
    case 'agreements':    return 'Agreements';
    case 'programs':      return 'Programs';
    case 'enrollments':   return 'Enrollments';
    case 'tracks':        return 'Track plans';
    case 'milestonedefs': return 'Milestone defs';
    case 'practices':     return 'Practices';
    case 'cpa52':         return 'CPA-52';
    case 'pages':         return 'Pages';
    case 'settings':      return 'Settings';
  }
}
